import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
  formatDateMMMddYYYY,
  getErrorResponse,
  toastError,
} from "../../utils/util";
import RdMyModal from "../../widgets/myModal/RdMyModal";
import AddFormItem from "./AddFormItem";
import AddIcon from "@mui/icons-material/Add";
import AirplanemodeInactiveIcon from "@mui/icons-material/AirplanemodeInactive";
import UpdateIcon from "@mui/icons-material/Update";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ADMIN, API_ENDPOINT } from "../../utils/constants";
import EditIcon from "@mui/icons-material/Edit"; // Ensure to install @mui/icons-material for icons
import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import CustomCircularP from "../../widgets/CustomCircularP";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useApiEp from "../../hooks/useApiEp";
import { getHeader } from "../../utils/utils2";

const FormItems = () => {
  const navigate = useNavigate();
  const [reloadItems, setReloadItems] = useState(false);
  const {
    data: formItems,
    isLoading,
    isError,
    error,
  } = useApiEp(`/admin/get-form-items?reload=${reloadItems}`);
  const [showAddItem, setShowAddItem] = useState(false);
  const [actionMode, setActionMode] = useState("add");
  const [showDeleteItem, setShowDeleteItem] = useState(false);
  const [deletingItem, setDeletingItem] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null); // State to control menu
  const [selectedItem, setSelectedItem] = useState(null); // Track selected item
  const open = Boolean(anchorEl);

  if (isLoading) {
    return <LinearProgress />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  const handleMenuClick = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item); // Set the selected item
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUpdateForm = (item) => {
    // Handle update form logic here
    handleMenuClose();
    navigate(`/oes-form-builder/${item._id}`);
  };

  const handleEditItem = (item) => {
    setActionMode("edit");
    setCurrentItem(item);
    handleMenuClose();
    setShowAddItem((prev) => !prev);
  };

  const handleDeleteItem = (item) => {
    setCurrentItem(item);
    handleMenuClose();
    setShowDeleteItem((prev) => !prev);
  };
  const handleAddItem = () => {
    setActionMode("add");
    setShowAddItem((prev) => !prev);
  };
  const onAddedItem = () => {
    setShowAddItem((prev) => !prev);
    setReloadItems((prev) => !prev);
  };
  const onYesDelete = async () => {
    setShowDeleteItem((prev) => !prev);
    if (!currentItem) {
      return;
    }
    try {
      setDeletingItem(true);
      const { data } = await axios.delete(
        `${API_ENDPOINT}/admin/delete-form-item?id=${currentItem._id}`,
        getHeader()
      );
      setDeletingItem(false);
      toast.success(data.message);
      setReloadItems((prev) => !prev);
    } catch (error) {
      toastError(error);
    } finally {
      setDeletingItem(false);
    }
  };
  const handleDisableForm = async (item) => {
    try {
      setDeletingItem(true);
      const { data } = await axios.post(
        `${API_ENDPOINT}/admin/disable-form-item?id=${item._id}`,
        { status: item.disabled !== true },
        getHeader()
      );
      setDeletingItem(false);
      toast.success(data.message);
      setReloadItems((prev) => !prev);
    } catch (error) {
      toastError(error);
    } finally {
      setDeletingItem(false);
    }
  };

  return (
    <Box>
      <CustomCircularP show={deletingItem} />
      <SimpleYesNoDialog
        title="Please confirm!"
        message="Are you sure you want to delete this?"
        show={showDeleteItem}
        setShow={setShowDeleteItem}
        onYes={onYesDelete}
      />
      <RdMyModal
        mWidth={"720px"}
        showModal={showAddItem}
        setShowModal={setShowAddItem}
        modalC={
          <AddFormItem
            show={showAddItem}
            onAdded={onAddedItem}
            mode={actionMode}
            editItem={currentItem}
          />
        }
      />
      <Stack direction={"row"} sx={{ marginBottom: 2 }}>
        <Box flex={1}></Box>
        <Button
          onClick={handleAddItem}
          size="small"
          startIcon={<AddIcon />}
          variant="outlined"
        >
          Add Form Item
        </Button>
      </Stack>
      {(formItems || []).length === 0 && (
        <Typography variant="h6" color={"#ccc"}>
          No form names yet
        </Typography>
      )}
      {formItems?.map((item, index) => (
        <Card
          key={index}
          sx={{
            bgcolor: item.disabled ? "rgba(0, 0, 0, 0.12)" : "initial",
            marginBottom: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 1,
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Stack direction={"row"} gap={2}>
              <Avatar>{item.serial}</Avatar>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {item.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {formatDateMMMddYYYY(item.expiry_date)}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
          <IconButton
            onClick={(event) => handleMenuClick(event, item)}
            aria-label="action"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open && selectedItem === item}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => handleUpdateForm(item)}>
              <ListItemIcon>
                <UpdateIcon fontSize="small" />
              </ListItemIcon>
              Update Form
            </MenuItem>
            <MenuItem onClick={() => handleEditItem(item)}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              Edit Item
            </MenuItem>
            <MenuItem onClick={() => handleDeleteItem(item)}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              Delete Item
            </MenuItem>
            <MenuItem onClick={() => handleDisableForm(item)}>
              <ListItemIcon>
                <AirplanemodeInactiveIcon
                  color={item.disabled ? "success" : "error"}
                  fontSize="small"
                />
              </ListItemIcon>
              {item.disabled ? "Enable form" : "Disable form"}
            </MenuItem>
          </Menu>
        </Card>
      ))}
    </Box>
  );
};

export default FormItems;
